import { markdown } from "@codemirror/lang-markdown";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import { Button, Flex, Segmented, Space, theme } from "antd";
import { type CSSProperties, useRef, useState } from "react";
import { insertCodeFence, prefixLines, resolveHeight, wrapSelection } from "./markdown-editor-edit";
import {
  DEFAULT_MARKDOWN_EDITOR_MESSAGES,
  type MarkdownEditorMode,
  type MarkdownEditorProps,
} from "./markdown-editor-messages";
import { renderMarkdownPreview } from "./markdown-editor-preview";

export type {
  MarkdownEditorAction,
  MarkdownEditorMessages,
  MarkdownEditorMode,
  MarkdownEditorProps,
} from "./markdown-editor-messages";

export function MarkdownEditor({
  value,
  onChange,
  height,
  readOnly = false,
  defaultMode = "split",
  messages,
}: MarkdownEditorProps) {
  const { token } = theme.useToken();
  const editorViewRef = useRef<EditorView | null>(null);
  const [mode, setMode] = useState<MarkdownEditorMode>(readOnly ? "preview" : defaultMode);
  const previewHeight = resolveHeight(height);
  const resolvedMessages = {
    emptyPreview: messages?.emptyPreview ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.emptyPreview,
    modes: {
      edit: messages?.modes?.edit ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.modes.edit,
      split: messages?.modes?.split ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.modes.split,
      preview: messages?.modes?.preview ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.modes.preview,
    },
    toolbar: {
      heading: messages?.toolbar?.heading ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.heading,
      bold: messages?.toolbar?.bold ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.bold,
      italic: messages?.toolbar?.italic ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.italic,
      quote: messages?.toolbar?.quote ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.quote,
      bulletList:
        messages?.toolbar?.bulletList ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.bulletList,
      orderedList:
        messages?.toolbar?.orderedList ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.orderedList,
      link: messages?.toolbar?.link ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.link,
      codeBlock: messages?.toolbar?.codeBlock ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.toolbar.codeBlock,
    },
    placeholders: {
      heading:
        messages?.placeholders?.heading ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.heading,
      bold: messages?.placeholders?.bold ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.bold,
      italic:
        messages?.placeholders?.italic ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.italic,
      quote: messages?.placeholders?.quote ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.quote,
      bulletList:
        messages?.placeholders?.bulletList ??
        DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.bulletList,
      orderedList:
        messages?.placeholders?.orderedList ??
        DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.orderedList,
      link: messages?.placeholders?.link ?? DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.link,
      codeBlock:
        messages?.placeholders?.codeBlock ??
        DEFAULT_MARKDOWN_EDITOR_MESSAGES.placeholders.codeBlock,
    },
  };
  const extensions: Extension[] = [markdown(), EditorState.tabSize.of(2), EditorView.lineWrapping];
  if (readOnly) {
    extensions.push(EditorState.readOnly.of(true), EditorView.editable.of(false));
  }

  const surfaceStyle = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    overflow: "hidden",
    background: token.colorBgContainer,
  } satisfies CSSProperties;

  const previewStyle = {
    minHeight: previewHeight,
    padding: 16,
    background: token.colorBgContainer,
    color: token.colorText,
    borderLeft: mode === "split" ? `1px solid ${token.colorBorderSecondary}` : undefined,
  } satisfies CSSProperties;

  const runToolbarAction = (action: () => void) => {
    const view = editorViewRef.current;
    if (!view || readOnly) {
      return;
    }
    action();
  };

  return (
    <div style={surfaceStyle}>
      {!readOnly ? (
        <Flex
          align="center"
          justify="space-between"
          gap={12}
          style={{
            padding: 12,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorFillAlter,
          }}
        >
          <Space wrap size={[8, 8]}>
            <Button
              size="small"
              title={resolvedMessages.toolbar.heading}
              onClick={() =>
                runToolbarAction(() =>
                  wrapSelection(
                    editorViewRef.current!,
                    "## ",
                    "",
                    resolvedMessages.placeholders.heading,
                  ),
                )
              }
            >
              H2
            </Button>
            <Button
              size="small"
              title={resolvedMessages.toolbar.bold}
              onClick={() =>
                runToolbarAction(() =>
                  wrapSelection(
                    editorViewRef.current!,
                    "**",
                    "**",
                    resolvedMessages.placeholders.bold,
                  ),
                )
              }
            >
              B
            </Button>
            <Button
              size="small"
              title={resolvedMessages.toolbar.italic}
              onClick={() =>
                runToolbarAction(() =>
                  wrapSelection(
                    editorViewRef.current!,
                    "*",
                    "*",
                    resolvedMessages.placeholders.italic,
                  ),
                )
              }
            >
              I
            </Button>
            <Button
              size="small"
              title={resolvedMessages.toolbar.quote}
              onClick={() =>
                runToolbarAction(() =>
                  prefixLines(
                    editorViewRef.current!,
                    () => "> ",
                    resolvedMessages.placeholders.quote,
                  ),
                )
              }
            >
              &gt;
            </Button>
            <Button
              size="small"
              title={resolvedMessages.toolbar.bulletList}
              onClick={() =>
                runToolbarAction(() =>
                  prefixLines(
                    editorViewRef.current!,
                    () => "- ",
                    resolvedMessages.placeholders.bulletList,
                  ),
                )
              }
            >
              -
            </Button>
            <Button
              size="small"
              title={resolvedMessages.toolbar.orderedList}
              onClick={() =>
                runToolbarAction(() =>
                  prefixLines(
                    editorViewRef.current!,
                    (index) => `${index + 1}. `,
                    resolvedMessages.placeholders.orderedList,
                  ),
                )
              }
            >
              1.
            </Button>
            <Button
              size="small"
              title={resolvedMessages.toolbar.link}
              onClick={() =>
                runToolbarAction(() =>
                  wrapSelection(
                    editorViewRef.current!,
                    "[",
                    "](https://example.com)",
                    resolvedMessages.placeholders.link,
                  ),
                )
              }
            >
              []()
            </Button>
            <Button
              size="small"
              title={resolvedMessages.toolbar.codeBlock}
              onClick={() => runToolbarAction(() => insertCodeFence(editorViewRef.current!))}
            >
              &lt;/&gt;
            </Button>
          </Space>
          <Segmented
            options={[
              { label: resolvedMessages.modes.edit, value: "edit" },
              { label: resolvedMessages.modes.split, value: "split" },
              { label: resolvedMessages.modes.preview, value: "preview" },
            ]}
            value={mode}
            onChange={(nextMode) => setMode(nextMode as MarkdownEditorMode)}
          />
        </Flex>
      ) : null}
      <div
        style={{
          display: mode === "split" ? "grid" : "block",
          gridTemplateColumns: mode === "split" ? "1fr 1fr" : undefined,
        }}
      >
        {mode !== "preview" ? (
          <CodeMirror
            value={value ?? ""}
            height={previewHeight}
            extensions={extensions}
            editable={!readOnly}
            onCreateEditor={(view) => {
              editorViewRef.current = view;
            }}
            onChange={(nextValue) => onChange?.(nextValue)}
            basicSetup={{
              lineNumbers: false,
              foldGutter: false,
              highlightActiveLine: !readOnly,
              highlightActiveLineGutter: false,
            }}
          />
        ) : null}
        {mode !== "edit" ? (
          <div style={previewStyle}>
            {renderMarkdownPreview(value ?? "", previewHeight, resolvedMessages.emptyPreview)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
